import { 
  Card, 
  Grid, 
  Typography, 
  Box, 
  Divider, 
  Chip, 
  Paper, 
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Tab,
  Tabs,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import StatusChangeDialog from "../../../../Components/Ticket/StatusChangeDialog";
import StatusHistoryTimeline from "../../../../Components/Ticket/StatusHistoryTimeline";
import AppService from "../../../../Configurations/Appservice";
import { API_PATHS } from "../../../../utils/constants";
import { 
  dismissLoader,
  showLoader, 
  showSuccessMessage,
  updateScreenTitle 
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { FORMAT_DATE } from "../../../../Configurations/JsUtil";
import UiDetailsItem from "./panels/UiDetailsItem";
import FieldValue from "./panels/FieldValue";

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";

const TicketDetails = () => {
  // State variables
  const [ticket, setTicket] = useState({});
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('basic-info'); // Default tab
  
  // Store processed document URLs to avoid showing them in other sections
  const processedDocumentUrls = new Map();
  
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  // Initial data loading
  useEffect(() => {
    dispatch(updateScreenTitle("Ticket Details"));
    fetchTicketDetails(true);
    
    return () => dispatch(updateScreenTitle(""));
  }, [auth, dispatch]);

  // Refresh ticket data
  const refreshTicket = () => {
    setIsRefreshing(true);
    fetchTicketDetails(true).finally(() => {
      setIsRefreshing(false);
    });
  };

  // Fetch ticket details from API
  const fetchTicketDetails = async (showRefreshing = false) => {
    if (showRefreshing) {
      dispatch(showLoader());
    }
    
    // Get ticketId from URL parameters
    const searchParams = new URLSearchParams(window.location.search);
    const ticketIdFromUrl = searchParams.get('ticketId');
    const ticketId = ticketIdFromUrl || location.state?.ticketId;
    
    if (!ticketId) {
      console.error('No ticketId provided');
      dispatch(dismissLoader());
      return;
    }
    
    let params = {
      ticketId: ticketId,
    };
    
    try {
      const ticketData = await AppService.get("v1/ticket/details", { params });
      console.log('API response ticket:', ticketData);
      setTicket(ticketData);
      
      // Fetch activity logs if needed
      fetchActivityLogs(ticketId);
      
      return ticketData;
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      return null;
    } finally {
      if (showRefreshing) {
        dispatch(dismissLoader());
      }
    }
  };
  
  // Fetch activity logs
  const fetchActivityLogs = async (ticketId) => {
    try {
      // Prefer the standardized plural endpoint with paging
      const logsData = await AppService.get(`v1/tickets/${ticketId}/logs?page=0&size=50`);
      // Normalize various possible response shapes
      let list = [];
      if (Array.isArray(logsData)) {
        list = logsData;
      } else if (logsData && Array.isArray(logsData.content)) {
        list = logsData.content;
      } else if (logsData && Array.isArray(logsData.data)) {
        list = logsData.data;
      }
      setActivityLogs(list);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setActivityLogs([]);
    }
  };

  // Helper function to get status color based on ticket status
  const getStatusColor = (status) => {
    if (!status) return 'info';
    
    switch(status.toUpperCase()) {
      case 'OPEN':
        return 'info';
      case 'IN_PROGRESS':
      case 'PENDING':
      case 'TAKEN':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'CLOSED':
        return 'default';
      default:
        return 'info';
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };
  
  // Claim/unclaim handlers
  const _onClaimComplete = () => {
    const ticketCopy = { ...ticket };
    ticketCopy.claimedBy = auth;
    setTicket(ticketCopy);
  };

  const _onUnClaimComplete = () => {
    const ticketCopy = { ...ticket };
    ticketCopy.claimedBy = null;
    setTicket(ticketCopy);
  };
  
  // Field rendering functions
  // Helper function to determine field type based on its properties
  const determineFieldType = (field) => {
    // If field already has a type, use it
    if (field.type) {
      return field.type;
    }
    
    // Otherwise, try to infer type from value or label/category
    const value = field.value?.toString().toLowerCase();
    const label = (field.label || field.category || '').toLowerCase();
    
    // Check for document fields
    if (value && (value.includes('ticket_documents/') || 
                 value.includes('.pdf') || 
                 value.includes('.jpg') || 
                 value.includes('.png') || 
                 value.includes('.doc') || 
                 value.includes('.docx'))) {
      return 'DOCUMENT';
    }
    
    // Check for email fields
    if (label.includes('email') || (value && value.includes('@') && value.includes('.'))) {
      return 'EMAIL';
    }
    
    // Check for phone fields
    if (label.includes('phone') || label.includes('mobile') || label.includes('contact')) {
      return 'PHONE';
    }
    
    // Check for selection fields
    if (label.includes('gender') || label.includes('type') || label.includes('status')) {
      return 'SELECT';
    }
    
    // Default to TEXT
    return 'TEXT';
  };
  
  // Enhance field with type information
  const enhanceField = (field) => {
    // Create a copy of the field
    const enhancedField = { ...field };
    
    // If type is null or undefined, determine it
    if (!enhancedField.type) {
      enhancedField.type = determineFieldType(field);
    }
    
    return enhancedField;
  };

  // Parse document field value if it's a string representation of an object
  const parseDocumentFieldValue = (value) => {
    if (!value) return null;
    
    // If it's already an object, return it
    if (typeof value === 'object') return value;
    
    // If it's a string that looks like an object representation
    if (typeof value === 'string' && 
        value.includes('isReference=true') && 
        value.includes('documentType=') && 
        value.includes('documentUrl=')) {
      
      try {
        // Convert string representation to object
        const docObj = {};
        // Extract key-value pairs
        const pairs = value.replace(/[{}]/g, '').split(', ');
        pairs.forEach(pair => {
          const [key, val] = pair.split('=');
          if (key && val) {
            docObj[key.trim()] = val.trim();
          }
        });
        
        return docObj;
      } catch (error) {
        console.error('Error parsing document field:', error);
        return null;
      }
    }
    
    return null;
  };
  
  // Check if field is a document type
  const isDocumentField = (field) => {
    if (!field) return false;
    
    const enhancedField = enhanceField(field);
    const type = enhancedField.type?.toUpperCase() || '';
    
    // Check by type
    if (type === 'DOCUMENT' || type === 'PDF' || type === 'FILE' || type === 'IMAGE') {
      return true;
    }

    // A "not found in employee documents" placeholder has no real file — treat as a document so it
    // stays out of the plain fields grid (the documents loop already skips these).
    const rawValue = typeof field?.value === 'string' ? field.value.toLowerCase() : '';
    if (rawValue.includes('notfoundinemployeedocs')) {
      return true;
    }

    // Check by field code/name. Ticket fields carry their identifier in label/category (code often
    // null), so fall back to those too.
    const fieldCode = (field.code || field.codeString || field.label || field.category || '').toLowerCase();
    if (['passportcopy', 'visapage', 'emiratesid', 'photo', 'birthcertificate', 'salarycertificate',
         'resume', 'educationalcertificate', 'experiencecertificate', 'policeclearancecertificate'].includes(fieldCode)) {
      return true;
    }
    
    // Check if value is a document reference string
    const value = field.value;
    if (typeof value === 'string' && 
        value.includes('isReference=true') && 
        value.includes('documentType=') && 
        value.includes('documentUrl=')) {
      return true;
    }
    
    return false;
  };
  
  // Check if field is a basic info field
  const isBasicInfoField = (field) => {
    const label = (field.label || field.title || field.code || field.codeString || '').toLowerCase();
    
    // Fields that should go under Basic Info
    return label.includes('service') || 
           label.includes('customer') || 
           label.includes('created by') || 
           label.includes('org') || 
           label.includes('organisation') || 
           label.includes('status');
  };

  const _renderTextFields = (fields) => {
    // Filter only non-document fields and enhance them with type
    // Also exclude fields that are already shown in the Documents tab
    const textFields = fields
      .filter(field => {
        // Skip document fields
        if (isDocumentField(field)) return false;
        
        // Skip basic info fields
        if (isBasicInfoField(field)) return false;
        
        // Skip fields whose values are already processed as documents
        if (field.value && processedDocumentUrls.has(field.value)) return false;
        
        return true;
      })
      .map(enhanceField);
    
    return textFields.map((field) => <FieldValue key={field.id || field.label} field={field} ticket={ticket} />);
  };
  
  const _renderBasicInfoFields = (fields) => {
    // Filter only basic info fields
    const basicInfoFields = fields
      .filter(field => isBasicInfoField(field))
      .map(enhanceField);
    
    return basicInfoFields.map((field) => <FieldValue key={field.id || field.label} field={field} ticket={ticket} />);
  };

  const _renderDocumentFields = (fields) => {
    // Filter document fields and enhance them with type
    // Exclude fields that are already shown in the Documents tab
    const docFields = fields
      .filter(field => {
        // Only include document fields
        if (!isDocumentField(field)) return false;
        
        // Skip fields whose values are already processed in the Documents tab
        if (field.value && processedDocumentUrls.has(field.value)) return false;
        
        return true;
      })
      .map(enhanceField);
    
    return docFields.map((field) => <FieldValue key={field.id || field.label} field={field} ticket={ticket} />);
  };

  const _renderUidDetails = (staticFields) => {
    // Filter out document fields and basic info fields
    const filteredStaticFields = staticFields.filter(field => {
      // Skip document fields
      if (isDocumentField(field)) return false;
      
      // Skip basic info fields
      if (isBasicInfoField(field)) return false;
      
      // Skip fields whose values are already processed as documents
      if (field.value && processedDocumentUrls.has(field.value)) return false;
      
      return true;
    });
    
    return filteredStaticFields.map((field) => {
      return <UiDetailsItem 
        key={field.code || field.codeString} 
        fieldContent={field.value} 
        detailsKey={field.code || field.codeString} 
        ticket={ticket} 
      />;
    });
  };
  
  const _renderBasicInfoStaticFields = (staticFields) => {
    // Filter only basic info fields
    const basicInfoFields = staticFields.filter(field => isBasicInfoField(field));
    
    return basicInfoFields.map((field) => {
      return <UiDetailsItem 
        key={field.code || field.codeString} 
        fieldContent={field.value} 
        detailsKey={field.code || field.codeString} 
        ticket={ticket} 
      />;
    });
  };
  
  // Document handling functions
  const _onSupportingFileChange = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // Handle file upload logic here
      dispatch(showLoader());
      
      // Normally you would upload the file to the server here
      // For now, just simulate success
      setTimeout(() => {
        dispatch(dismissLoader());
        dispatch(showSuccessMessage("Document uploaded successfully"));
        refreshTicket();
      }, 1000);
    }
  };
  
  // Document section rendering
  const _renderDocumentsSection = () => {
    const supportingDocs = ticket?.supportingDocs || [];
    const documentFields = ticket?.fields ? 
      ticket.fields.filter(field => isDocumentField(field)) : [];
    const staticDocumentFields = ticket?.staticFields ? 
      ticket.staticFields.filter(field => isDocumentField(field)) : [];
    const employeeDocuments = Array.isArray(ticket?.employeeDocuments) ? ticket.employeeDocuments : [];

    // If employeeDocuments are present, render ONLY those to avoid legacy/field-based duplicates
    if (employeeDocuments.length > 0) {
      const allDocuments = [];
      const uniqueDocUrls = new Map();
      employeeDocuments.forEach(empDoc => {
        if (!empDoc || !empDoc.filePath) return;
        const filePath = empDoc.filePath;
        const url = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(filePath)}`;
        if (!uniqueDocUrls.has(url)) {
          uniqueDocUrls.set(url, true);
          processedDocumentUrls.set(url, true);
          allDocuments.push({
            documentUrl: url,
            comment: empDoc.documentType || 'Employee Document',
            fieldType: 'employee',
            fileName: empDoc.fileName || (filePath.split('/').pop()) || 'Document'
          });
        }
      });

      // Also include directly-uploaded (dynamic / field-level) documents that carry a real file path
      // and are NOT employee-document references (those are already covered by employeeDocuments above),
      // so workflow docs uploaded on the request also show here.
      const addFieldDoc = (field) => {
        const parsed = parseDocumentFieldValue(field.value);
        if (parsed && parsed.isReference) return; // linked employee doc — already shown
        let docUrl = parsed ? parsed.documentUrl : field.value;
        if (!docUrl || typeof docUrl !== 'string' || docUrl.trim().startsWith('{')) return; // sentinel/unresolved
        // Wrap a raw S3 path in the authenticated download endpoint (leave real URLs as-is).
        if (!(/^https?:\/\//i.test(docUrl) || docUrl.startsWith('/v1/') || docUrl.startsWith('/api/') || docUrl.includes('/files/download'))) {
          docUrl = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(docUrl)}`;
        }
        if (uniqueDocUrls.has(docUrl)) return;
        uniqueDocUrls.set(docUrl, true);
        processedDocumentUrls.set(docUrl, true);
        allDocuments.push({
          documentUrl: docUrl,
          comment: field.label || field.title || field.code || field.codeString,
          // no fieldType: the shared render tags any fieldType-bearing doc as "(Employee)"
          fileName: (parsed && (parsed.fileName || parsed.originalName)) || field.fileName || field.name || 'Document'
        });
      };
      documentFields.forEach(addFieldDoc);
      staticDocumentFields.forEach(addFieldDoc);

      if (!allDocuments.length) {
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
              No documents attached to this ticket
            </Typography>
          </Box>
        );
      }

      return (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Attached Documents
          </Typography>
          <Grid container spacing={2}>
            {allDocuments.map((doc, index) => {
              let FileIcon = InsertDriveFileIcon;
              if (doc.documentUrl?.toLowerCase().endsWith('.pdf')) {
                FileIcon = PictureAsPdfIcon;
              } else if (/(\.(jpe?g|png|gif|bmp)$)/i.test(doc.documentUrl || '')) {
                FileIcon = ImageIcon;
              }
              return (
                <Grid item xs={12} sm={6} md={4.28} key={index}>
                  <Paper 
                    elevation={0} 
                    variant="outlined"
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      alignItems: 'center',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: '#e6f0f0' }
                    }}
                  >
                    <Box sx={{ 
                      mr: 2, 
                      bgcolor: '#f0f0f0', 
                      borderRadius: '50%',
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#064d51'
                    }}>
                      <FileIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography noWrap variant="body1" sx={{ fontWeight: 500 }}>
                        {doc.fileName || doc.documentUrl?.split('/').pop() || 'Document'}
                      </Typography>
                      {doc.comment && (
                        <Typography noWrap variant="caption" color="text.secondary">
                          {doc.comment}
                        </Typography>
                      )}
                      {doc.fieldType && (
                        <Typography noWrap variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.7rem' }}>
                          (Employee)
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <IconButton size="small" href={doc.documentUrl} target="_blank" title="View document">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" href={doc.documentUrl} download title="Download document">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    }

    // Fallback: legacy behavior when employeeDocuments are not present
    // Create a map to track unique document URLs
    const uniqueDocUrls = new Map();
    // Process all document sources, keeping only unique documents
    const allDocuments = [];
    // Process supporting docs first (highest priority)
    supportingDocs.forEach(doc => {
      if (doc && doc.documentUrl && !uniqueDocUrls.has(doc.documentUrl)) {
        uniqueDocUrls.set(doc.documentUrl, true);
        processedDocumentUrls.set(doc.documentUrl, true); // Mark as processed
        allDocuments.push(doc);
      }
    });
    // Process regular document fields next (medium priority)
    documentFields.forEach(field => {
      const docUrl = field.value;
      if (docUrl && !uniqueDocUrls.has(docUrl)) {
        uniqueDocUrls.set(docUrl, true);
        processedDocumentUrls.set(docUrl, true); // Mark as processed
        allDocuments.push({
          documentUrl: docUrl,
          comment: field.label || field.title,
          fieldType: 'regular',
          fileName: field.fileName || field.name || docUrl.split('/').pop() || 'Document'
        });
      }
    });
    // Process static document fields
    staticDocumentFields.forEach(field => {
      const parsedDocData = parseDocumentFieldValue(field.value);
      const docUrl = parsedDocData ? parsedDocData.documentUrl : field.value;
      // Skip placeholder/sentinel values like {"notFoundInEmployeeDocs":true} (and any JSON-object
      // string that didn't resolve to a real URL) — they aren't downloadable and would otherwise
      // render as a broken document link.
      const isUnresolvable = typeof docUrl === 'string' && docUrl.trim().startsWith('{');
      if (docUrl && !isUnresolvable && !uniqueDocUrls.has(docUrl)) {
        uniqueDocUrls.set(docUrl, true);
        processedDocumentUrls.set(docUrl, true); // Mark as processed
        const fieldCode = (field.code || field.codeString || '').toLowerCase();
        const isDocumentType = ['salarycertificate', 'passport', 'visa', 'emiratesid', 'photo', 'labourcontract', 'birthcertificate'].includes(fieldCode);
        const fileName = parsedDocData ? 
          (parsedDocData.fileName || parsedDocData.originalName || docUrl.split('/').pop() || 'Document') : 
          (field.fileName || field.name || docUrl.split('/').pop() || 'Document');
        allDocuments.push({
          documentUrl: docUrl,
          comment: field.code || field.codeString,
          fieldType: 'static',
          documentType: isDocumentType ? fieldCode : undefined,
          fileName: fileName
        });
      }
    });
    
    // Log the deduplication results
    console.log(`Documents after deduplication: ${allDocuments.length} (from ${supportingDocs.length + documentFields.length + staticDocumentFields.length} total)`);

    
    if (!allDocuments.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
            No documents attached to this ticket
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Attached Documents
        </Typography>
        <Grid container spacing={2}>
          {allDocuments.map((doc, index) => {
            // Determine icon based on file type
            let FileIcon = InsertDriveFileIcon;
            if (doc.documentUrl?.toLowerCase().endsWith('.pdf')) {
              FileIcon = PictureAsPdfIcon;
            } else if (/\.(jpe?g|png|gif|bmp)$/i.test(doc.documentUrl || '')) {
              FileIcon = ImageIcon;
            }
            
            return (
              <Grid item xs={12} sm={6} md={4.28} key={index}>
                <Paper 
                  elevation={0} 
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#e6f0f0' }
                  }}
                >
                  <Box sx={{ 
                    mr: 2, 
                    bgcolor: '#f0f0f0', 
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#064d51'
                  }}>
                    <FileIcon />
                  </Box>
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <Typography noWrap variant="body1" sx={{ fontWeight: 500 }}>
                      {doc.fileName || doc.documentUrl?.split('/').pop() || 'Document'}
                    </Typography>
                    {doc.comment && (
                      <Typography noWrap variant="caption" color="text.secondary">
                        {doc.comment}
                      </Typography>
                    )}
                    {doc.fieldType && (
                      <Typography noWrap variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.7rem' }}>
                        {doc.fieldType === 'static' ? '(Static Field)' : doc.fieldType === 'regular' ? '(Field)' : ''}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" href={doc.documentUrl} target="_blank" title="View document">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" href={doc.documentUrl} download title="Download document">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };
  
  // Activity log rendering
  const renderActivityLogs = () => {
    if (!activityLogs || activityLogs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
            No activity logs available for this ticket
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Activity History
        </Typography>
        
        <Box sx={{ 
          borderLeft: '2px solid #e0e0e0', 
          pl: 2, 
          ml: 2,
          position: 'relative'
        }}>
          {activityLogs.map((log, index) => (
            <Box 
              key={index} 
              sx={{ 
                position: 'relative',
                pb: 3,
                '&:last-child': { pb: 0 }
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  left: -26,
                  top: 0,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#064d51',
                  border: '2px solid white'
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                {FORMAT_DATE(log.occurredAt || log.createdAt)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                {log.actionSummary || log.message || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                by {log.actorName || log.userName || 'System'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
  
  // Assignment form rendering
  const renderAssignmentForm = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Ticket Assignment
        </Typography>
        
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
            Assignment options will appear here based on ticket status and your permissions
          </Typography>
        </Box>
      </Box>
    );
  };

  if (!ticket) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Typography variant="h6" color="text.secondary">
          Loading ticket details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Top information card */}
      <Card sx={{ mb: 3, borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ 
          bgcolor: '#064d51', 
          color: 'white', 
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
            Ticket #{ticket.uid}
          </Typography>
          <IconButton 
            size="small" 
            onClick={refreshTicket}
            disabled={isRefreshing}
            sx={{ color: 'white' }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>              
                <Chip 
                  label={ticket.status || 'Unknown'} 
                  size="small" 
                  color={getStatusColor(ticket.status)}
                  sx={{
                    textTransform: 'capitalize',
                    fontWeight: '500',
                    fontSize: '0.75rem',
                    height: '24px',
                    borderRadius: '4px',
                  }}
                />
                {ticket?.status && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setStatusDialogOpen(true)}
                    sx={{
                      fontSize: '0.75rem',
                      height: '28px',
                      padding: '0 12px',
                      minWidth: 'auto',
                      ml: 1,
                      // Show for all statuses including CLOSED and COMPLETED
                      display: 'inline-flex',
                    }}
                  >
                    Change Status
                  </Button>
                )}
              </Box>
            </Box>
            
            <Box>
              {ticket.claimedBy ? (
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  onClick={_onUnClaimComplete}
                >
                  Unclaim Ticket
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small"
                  onClick={_onClaimComplete}
                  sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#053a3d' } }}
                >
                  Claim Ticket
                </Button>
              )}
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>Service</Typography>
              <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                {ticket?.service?.name || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>Customer</Typography>
              <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                {ticket?.uid || 'N/A'}
              </Typography>
            </Grid>

            {/* Regular fields excluding Created Date, Created By, and Item */}
            {ticket.fields && ticket.fields.filter(field => {
              const label = (field.label || field.title || '').toLowerCase();
              return !isDocumentField(field) && 
                     !isBasicInfoField(field) && 
                     !label.includes('created date') && 
                     !label.includes('created by') && 
                     !label.includes('item');
            }).map((field, index) => (
              <Grid item xs={12} sm={6} md={3} key={`field-${index}`}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>{field.label || field.title}</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                  {field.value || 'N/A'}
                </Typography>
              </Grid>
            ))}
            
            {/* Static fields excluding Created Date, Created By, and Item */}
            {ticket.staticFields && ticket.staticFields.filter(field => {
              const code = (field.code || field.codeString || '').toLowerCase();
              return !isDocumentField(field) && 
                     !isBasicInfoField(field) && 
                     !code.includes('created date') && 
                     !code.includes('created by') && 
                     !code.includes('item');
            }).map((field, index) => (
              <Grid item xs={12} sm={6} md={3} key={`static-field-${index}`}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>{field.code || field.codeString}</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                  {field.value || 'N/A'}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>
      
      {/* Main content with tabs */}
      <Card sx={{ borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ 
          bgcolor: '#064d51', 
          color: 'white', 
          p: 2
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
            Details
          </Typography>
        </Box>
        
        <Box sx={{ p: 0 }}>
          {/* Tab navigation */}
          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
            <Box 
              onClick={() => handleTabChange('basic-info')}
              sx={{ 
                py: 1.5, 
                px: 3, 
                cursor: 'pointer',
                borderBottom: activeTab === 'basic-info' ? 2 : 0,
                borderColor: activeTab === 'basic-info' ? '#064d51' : 'transparent',
                color: activeTab === 'basic-info' ? '#064d51' : 'text.secondary',
                fontWeight: activeTab === 'basic-info' ? 600 : 400,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                Basic Info
              </Typography>
            </Box>

            <Box
              onClick={() => handleTabChange('actions')}
              sx={{ 
                py: 1.5, 
                px: 3, 
                cursor: 'pointer',
                borderBottom: activeTab === 'actions' ? 2 : 0,
                borderColor: activeTab === 'actions' ? '#064d51' : 'transparent',
                color: activeTab === 'actions' ? '#064d51' : 'text.secondary',
                fontWeight: activeTab === 'actions' ? 600 : 400,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                Actions
              </Typography>
            </Box>
            
            <Box 
              onClick={() => handleTabChange('documents')}
              sx={{ 
                py: 1.5, 
                px: 3, 
                cursor: 'pointer',
                borderBottom: activeTab === 'documents' ? 2 : 0,
                borderColor: activeTab === 'documents' ? '#064d51' : 'transparent',
                color: activeTab === 'documents' ? '#064d51' : 'text.secondary',
                fontWeight: activeTab === 'documents' ? 600 : 400,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                Documents
              </Typography>
            </Box>
            
            <Box 
              onClick={() => handleTabChange('history')}
              sx={{ 
                py: 1.5, 
                px: 3, 
                cursor: 'pointer',
                borderBottom: activeTab === 'history' ? 2 : 0,
                borderColor: activeTab === 'history' ? '#064d51' : 'transparent',
                color: activeTab === 'history' ? '#064d51' : 'text.secondary',
                fontWeight: activeTab === 'history' ? 600 : 400,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                History
              </Typography>
            </Box>
            
            <Box 
              onClick={() => handleTabChange('assignment')}
              sx={{ 
                py: 1.5, 
                px: 3, 
                cursor: 'pointer',
                borderBottom: activeTab === 'assignment' ? 2 : 0,
                borderColor: activeTab === 'assignment' ? '#064d51' : 'transparent',
                color: activeTab === 'assignment' ? '#064d51' : 'text.secondary',
                fontWeight: activeTab === 'assignment' ? 600 : 400,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                Assignment
              </Typography>
            </Box>
          </Box>
          
          {/* Tab content */}
          <Box sx={{ p: 3 }}>
            {activeTab === 'basic-info' && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  {/* Created Date */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Created Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                      {ticket?.createdAt && FORMAT_DATE(ticket.createdAt) || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* Created By */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Created By
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                      {ticket?.createdBy || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* Item fields */}
                  {ticket.fields && ticket.fields.filter(field => {
                    const label = (field.label || field.title || '').toLowerCase();
                    return label.includes('item');
                  }).map((field, index) => (
                    <Grid item xs={12} sm={6} key={`item-field-${index}`}>
                      <Typography variant="body2" color="textSecondary">
                        {field.label || field.title}
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                        {field.value || 'N/A'}
                      </Typography>
                    </Grid>
                  ))}
                  
                  {/* Item static fields */}
                  {ticket.staticFields && ticket.staticFields.filter(field => {
                    const code = (field.code || field.codeString || '').toLowerCase();
                    return code.includes('item');
                  }).map((field, index) => (
                    <Grid item xs={12} sm={6} key={`item-static-field-${index}`}>
                      <Typography variant="body2" color="textSecondary">
                        {field.code || field.codeString}
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                        {field.value || 'N/A'}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                
                <Typography variant="subtitle1" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                  Description
                </Typography>
                <Box>
                  {ticket.description ? (
                    <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                      {ticket.description}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                      No description provided for this ticket.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {activeTab === 'actions' && (
              <Box>
                <Typography 
                  variant='subtitle1'
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  Actions
                </Typography>
                {ticket?.actions?.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><b>Action</b></TableCell>
                          <TableCell><b>Status</b></TableCell>
                          <TableCell><b>Due Date</b></TableCell>
                          <TableCell><b>remarks</b> </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { ticket.actions.map((action,index) => (
                          <TableRow key={index}>
                            <TableCell>{action?.action || "N/A"}</TableCell>
                            <TableCell>{action?.status || "N/A"}</TableCell>
                            <TableCell>{action?.dueDate ? FORMAT_DATE(action.dueDate) : "N/A" }</TableCell>
                            <TableCell>{action?.remarks || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No actions available for this ticket.
                  </Typography>
                )}
              </Box>
            )}
            
            {activeTab === 'documents' && (
              <Box>
                {/* Ticket Documents Section */}
                {_renderDocumentsSection()}
                
                {/* Tabs for Details, History, etc. */}
                <Card sx={{ mt: 3, boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, newValue) => setTabValue(newValue)}
                      aria-label="ticket detail tabs"
                    >
                      <Tab label="Activity" />
                      <Tab label="Status History" />
                    </Tabs>
                  </Box>
                  
                  {/* Tab 1: Activity Logs */}
                  {tabValue === 0 && (
                    <Box sx={{ p: 3 }}>
                      {activityLogs.length > 0 ? (
                        <Box>
                          {/* Your existing activity logs rendering */}
                          <Typography variant="body2">{activityLogs.length} activities found</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No activity logs available for this ticket.
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Tab 2: Status History */}
                  {tabValue === 1 && (
                    <Box sx={{ p: 3 }}>
                      {ticket?.id ? (
                        <StatusHistoryTimeline ticketId={ticket.id} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Status history is not available for this ticket.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Card>
                
                {/* Status Change Dialog */}
                <StatusChangeDialog
                  open={statusDialogOpen}
                  onClose={() => setStatusDialogOpen(false)}
                  ticket={ticket}
                  onStatusChanged={(newStatus) => {
                    refreshTicket();
                    // Force switch to the status history tab to show the change
                    setTabValue(1);
                  }}
                />
                
                {/* Document upload section */}
                {ticket?.status && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      Upload New Documents
                    </Typography>
                    
                    <Box
                      component="label"
                      htmlFor="upload-document"
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#064d51',
                          bgcolor: 'rgba(6, 77, 81, 0.05)'
                        }
                      }}
                    >
                      <input
                        type="file"
                        id="upload-document"
                        style={{ display: 'none' }}
                        onChange={_onSupportingFileChange}
                        ref={fileInputRef}
                        multiple
                      />
                      <FileUploadIcon sx={{ fontSize: 40, color: '#064d51', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }} align="center">
                        Drag & drop files here or click to browse
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary">
                        Supported file types: PDF, JPG, PNG, DOC
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            
            {activeTab === 'history' && (
              <Box>
                {renderActivityLogs()}
              </Box>
            )}
            
            {activeTab === 'assignment' && (
              <Box>
                {renderAssignmentForm()}
              </Box>
            )}
          </Box>
        </Box>
      </Card>
      
      {/* Display remark if available */}
      {ticket?.remark && (
        <Card sx={{ mt: 3, borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{ p: 3 }}>
            <Typography color="error">
              Remark: {ticket.remark}
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default TicketDetails;
